import { useCallback, useRef } from 'react'
import type { FieldErrors, FieldPath, FieldValues, UseFormReturn } from 'react-hook-form'

/**
 * The validation errors as they stand right now.
 *
 * Not `form.formState.errors`: that is a Proxy which only tracks the
 * properties read during a render, and nothing renders `formState.errors` from
 * this hook's component — each field subscribes to its own error through
 * `useFormState`. Read from here and it comes back empty, which is why focus
 * silently went nowhere on every form.
 *
 * `_formState` is internal to react-hook-form, but it is the only source
 * updated synchronously by `trigger()`; subscribing instead would race the
 * re-render that `trigger` schedules. The spec covering focus order is what
 * catches an upgrade that moves this.
 */
function currentErrors<T extends FieldValues>(form: UseFormReturn<T>): FieldErrors<T> {
  const control = form.control as unknown as { _formState?: { errors?: FieldErrors<T> } }
  return control._formState?.errors ?? form.formState.errors
}

/**
 * The dotted path of the first field in error, depth-first.
 *
 * `Object.keys(errors)[0]` is not enough: the schemas here nest to mirror the
 * params hash Rails receives, so the top-level key is the container —
 * `'user'` — and no field answers to that. Focus then quietly went nowhere.
 * Errors nest to match the field path, so walk down to the first leaf, which
 * is the node carrying a `message`.
 */
function firstErrorPath(errors: unknown, prefix = ''): string | undefined {
  if (!errors || typeof errors !== 'object') return undefined

  const node = errors as Record<string, unknown>
  if (typeof node.message === 'string') return prefix || undefined

  // Array indices come back as keys here too, so field arrays walk the same way.
  for (const key of Object.keys(node)) {
    const found = firstErrorPath(node[key], prefix ? `${prefix}.${key}` : key)
    if (found) return found
  }

  return undefined
}

/**
 * Gates a natively-posted form on client validation without taking the submit
 * away from Turbo.
 *
 * Most forms here post straight to Rails with an `authenticity_token` rather
 * than fetching, and Turbo drives that submit — a 422 re-render depends on it.
 * `handleSubmit` would swallow the event, and calling `form.submit()` after
 * validating would skip Turbo's listener and force a full page load.
 *
 * So: block the first submit, validate, and if the form is clean re-fire it.
 * That second pass sets the flag, falls straight through this handler without
 * calling `preventDefault`, and reaches Turbo as an ordinary submit.
 */
export function useNativeFormSubmit<T extends FieldValues>(
  form: UseFormReturn<T>,
  { onValid }: { onValid?: (values: T) => void } = {},
) {
  const passthroughRef = useRef(false)

  const onSubmit = useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      if (passthroughRef.current) {
        passthroughRef.current = false
        return
      }

      event.preventDefault()
      const formElement = event.currentTarget

      const valid = await form.trigger()
      if (!valid) {
        // `trigger` validates but never moves focus, unlike a blocked
        // `handleSubmit`. Send the user to the first problem themselves.
        //
        // Guarded: not every field is a real input. A value tracked with
        // `setValue` alone — a combobox selection, say — has no ref for
        // `setFocus` to reach, and reaching for one anyway throws and takes
        // the whole submit handler down with it.
        const firstInvalid = firstErrorPath(currentErrors(form))
        if (firstInvalid) {
          try {
            form.setFocus(firstInvalid as FieldPath<T>)
          } catch {
            // No focusable ref; the inline message still marks the field.
          }
        }
        return
      }

      onValid?.(form.getValues())
      passthroughRef.current = true

      // Deferred by a task, and it has to be. `requestSubmit()` is specified to
      // do nothing while the form is already firing submission events, and
      // awaiting validation resumes inside that same dispatch — so calling it
      // straight through returns silently and the form never posts at all.
      // Yielding to the next task lets the first submit finish unwinding,
      // after which this fires a real event that Turbo picks up.
      setTimeout(() => formElement.requestSubmit(), 0)
    },
    [form, onValid],
  )

  return { onSubmit, noValidate: true }
}
